import { Lightbulb, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useMemo } from "react";
import type { OrderAlertProps } from "./order-alert.types";
import { ALERT_CONTENT, SESSION_STORAGE_KEY } from "./order-alert.config";
import {
  getAlertState,
  isValidVariant,
  openUrlSafely,
} from "./order-alert.utils";
import { useAuthGuard } from "@/hooks/use-auth-guard";

export function OrderAlert({
  variant = "create",
  className = "",
}: OrderAlertProps) {
  const state = getAlertState();
  const [isVisible, setIsVisible] = useState(state.shouldShow);
  const [canClose] = useState(state.canClose);
  const { isExternalUser } = useAuthGuard();

  // Validação de variant e memoização do conteúdo
  const safeVariant = useMemo(() => {
    return isValidVariant(variant) ? variant : "create";
  }, [variant]);

  const variantContent = useMemo(() => {
    return ALERT_CONTENT[safeVariant];
  }, [safeVariant]);

  const handleClose = () => {
    if (canClose) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
      setIsVisible(false);
    }
  };

  const handleFirstButtonClick = () => {
    if (variantContent.firstButtonLink) {
      openUrlSafely(variantContent.firstButtonLink);
    }
  };

  const handleSecondButtonClick = () => {
    if (variantContent.secondButtonLink) {
      openUrlSafely(variantContent.secondButtonLink);
    }
  };

  if (!isVisible || isExternalUser) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      aria-label="Alerta informativo sobre propostas customizadas"
      className={`border rounded-lg p-4 flex gap-3 items-start relative ${className} ${variantContent.bgColor}`}
    >
      {canClose && (
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-3 right-3 text-[#6B5E4F] hover:text-[#4A3F2F] transition-colors"
          aria-label="Fechar alerta"
        >
          <X className="h-4 w-4" />
        </button>
      )}
      <Lightbulb
        className="h-5 w-5 text-[#6B5E4F] flex-shrink-0 mt-0.5"
        aria-hidden="true"
      />
      <div className="flex-1 flex flex-col gap-2">
        <p className="font-medium text-sm text-[#3D3329]">
          {variantContent.title}
        </p>
        <p
          className={`text-sm text-[#6B5E4F] leading-relaxed ${
            variantContent.showExtraInfo ? "mb-1" : ""
          }`}
        >
          {variantContent.description}
        </p>
        {variantContent.showExtraInfo && (
          <p className="text-xs text-[#6B5E4F] leading-relaxed">
            <span className="font-medium">
              O que é uma proposta customizada:{" "}
            </span>
            <span>
              É o registro usado para orçar salas privativas, e propostas passam
              a ter esse nome na plataforma.
            </span>
          </p>
        )}
        <div className="flex gap-4 mt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={handleFirstButtonClick}
            className="bg-white text-[#3D3329] border-[#D4C5A0] hover:bg-[#FAFAF5] h-8 text-sm"
            type="button"
          >
            {variantContent.firstButtonText}
          </Button>
          {variantContent.showSecondButton &&
            variantContent.secondButtonText && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSecondButtonClick}
                className="bg-transparent text-[#3D3329] border-none hover:bg-transparent hover:text-[#4A3F2F] h-8 text-sm p-0 shadow-none"
                type="button"
              >
                {variantContent.secondButtonText}
              </Button>
            )}
        </div>
      </div>
    </div>
  );
}
